import { cva } from 'class-variance-authority'

/*
 * Ported from shadcn's recipe (`bases/base` registry). The `--drawer-*` and `--nested-drawers` custom
 * properties are emitted by Base UI, not by us. Durations expressed as `calc(var(--drawer-swipe-strength)*400ms)`
 * are derived from gesture velocity: they can't become tokens and stay literal.
 *
 * shadcn propagates a `data-swipe-axis` through a React context; unnecessary here, Base UI already sets
 * `data-swipe-direction` on the popup. Axis rules are therefore written as `:is(left, right)` / `:is(up, down)`,
 * literally, to stay extractable by Tailwind.
 *
 * Enter uses a keyframe animation (like Dialog), not a data-starting-style transition: a keyframe plays
 * whenever the popup is inserted into the DOM, so it animates even when a consumer mounts the drawer
 * already open (route-driven panels) -- a transition needs a real prop flip it can't rely on. Exit stays
 * a transition because a swipe release must interpolate from wherever the finger let go, which a keyframe
 * (fixed start point) can't express. Don't "harmonise" these into the same mechanism.
 */
const popupVariants = cva(
  `
    group/drawer-popup pointer-events-auto fixed z-50 m-[var(--drawer-inset,0px)] flex h-[var(--drawer-content-height)]
    max-h-[var(--drawer-content-max-height,none)] min-h-0 w-[var(--drawer-content-width,auto)]
    transform-[translate3d(var(--translate-x,0px),var(--translate-y,0px),0)_scale(var(--stack-scale))] flex-col
    bg-surface-base-normal shadow-bottom-30 transition-[transform,height,opacity,filter] motion-enter-emphasized
    will-change-transform outline-none select-none [--bleed:3rem] [--drawer-content-height:var(--drawer-height,auto)]
    [--peek:1rem] [--stack-height:var(--drawer-frontmost-height,var(--drawer-height,0px))]
    [--stack-peek-offset:max(0px,calc((var(--nested-drawers)-var(--stack-progress))*var(--peek)))]
    [--stack-progress:clamp(0,var(--drawer-swipe-progress),1)]
    [--stack-scale-base:max(0,calc(1-(var(--nested-drawers)*var(--stack-step))))]
    [--stack-scale:clamp(0,calc(var(--stack-scale-base)+(var(--stack-step)*var(--stack-progress))),1)]
    [--stack-shrink:calc(1-var(--stack-scale))] [--stack-step:0.05] [interpolate-size:allow-keywords]
    after:pointer-events-none after:absolute after:bg-[var(--drawer-bleed-background,var(--color-surface-base-normal))]
    data-ending-style:transform-[var(--closed-transform)] data-ending-style:opacity-[0.9999]
    data-ending-style:motion-exit-emphasized
    data-nested-drawer-open:overflow-hidden data-nested-drawer-open:brightness-95
    data-nested-drawer-swiping:duration-0
    data-ending-style:data-nested-drawer-swiping:duration-[calc(var(--drawer-swipe-strength)*400ms)]
    data-swiping:duration-0
    data-ending-style:data-swiping:duration-[calc(var(--drawer-swipe-strength)*400ms)]
    data-[open]:animate-in data-[open]:motion-enter-emphasized
    data-[swipe-direction=down]:bottom-0 data-[swipe-direction=down]:origin-bottom
    data-[swipe-direction=down]:[--closed-transform:translate3d(0,calc(100%+var(--drawer-inset,0px)+2px),0)]
    data-[swipe-direction=down]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)-var(--stack-peek-offset)-(var(--stack-shrink)*var(--stack-height)))]
    data-[swipe-direction=down]:after:top-full
    data-[swipe-direction=down]:data-[open]:slide-in-from-bottom
    data-[swipe-direction=left]:left-0 data-[swipe-direction=left]:origin-left
    data-[swipe-direction=left]:[--closed-transform:translate3d(calc(-100%-var(--drawer-inset,0px)-2px),0,0)]
    data-[swipe-direction=left]:[--translate-x:calc(var(--drawer-swipe-movement-x)+var(--stack-peek-offset)+(var(--stack-shrink)*100%))]
    data-[swipe-direction=left]:after:right-full
    data-[swipe-direction=left]:data-[open]:slide-in-from-left
    data-[swipe-direction=right]:right-0 data-[swipe-direction=right]:origin-right
    data-[swipe-direction=right]:[--closed-transform:translate3d(calc(100%+var(--drawer-inset,0px)+2px),0,0)]
    data-[swipe-direction=right]:[--translate-x:calc(var(--drawer-swipe-movement-x)-var(--stack-peek-offset)-(var(--stack-shrink)*100%))]
    data-[swipe-direction=right]:after:left-full
    data-[swipe-direction=right]:data-[open]:slide-in-from-right
    data-[swipe-direction=up]:top-0 data-[swipe-direction=up]:origin-top
    data-[swipe-direction=up]:[--closed-transform:translate3d(0,calc(-100%-var(--drawer-inset,0px)-2px),0)]
    data-[swipe-direction=up]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)+var(--stack-peek-offset)+(var(--stack-shrink)*var(--stack-height)))]
    data-[swipe-direction=up]:after:bottom-full
    data-[swipe-direction=up]:data-[open]:slide-in-from-top
    [&:is([data-swipe-direction=left],[data-swipe-direction=right])]:inset-y-0
    [&:is([data-swipe-direction=left],[data-swipe-direction=right])]:flex-row
    [&:is([data-swipe-direction=left],[data-swipe-direction=right])]:[--drawer-content-width:100%]
    [&:is([data-swipe-direction=left],[data-swipe-direction=right])]:after:inset-y-0
    [&:is([data-swipe-direction=left],[data-swipe-direction=right])]:after:w-[var(--bleed)]
    [&:is([data-swipe-direction=up],[data-swipe-direction=down])]:inset-x-0
    [&:is([data-swipe-direction=up],[data-swipe-direction=down])]:[--drawer-content-max-height:calc(100dvh-6rem)]
    [&:is([data-swipe-direction=up],[data-swipe-direction=down])]:after:inset-x-0
    [&:is([data-swipe-direction=up],[data-swipe-direction=down])]:after:h-[var(--bleed)]
    [&:is([data-swipe-direction=up],[data-swipe-direction=down])]:data-nested-drawer-open:h-[var(--stack-height)]
  `,
  {
    variants: {
      // Panel width. Only applies to horizontal directions: on a vertical drawer the width is full and
      // it's the height that matters (capped at calc(100dvh-6rem) in the base).
      // Full width under `md`, product decision from 2026-07-30.
      size: {
        small: 'md:[&:is([data-swipe-direction=left],[data-swipe-direction=right])]:[--drawer-content-width:384px]',
        medium: 'md:[&:is([data-swipe-direction=left],[data-swipe-direction=right])]:[--drawer-content-width:480px]',
        large: 'md:[&:is([data-swipe-direction=left],[data-swipe-direction=right])]:[--drawer-content-width:560px]',
      },
    },
    defaultVariants: { size: 'medium' },
  },
)

export { popupVariants }
